<script setup lang="ts">
import {computed, ref} from "vue";
import dayjs, {Dayjs} from "dayjs";
import "dayjs/locale/fr";
import {useRouter} from "vue-router";
import {useToastStore} from "@/stores/toast.store";
import {teamPlanningApi} from "@/container";
import {JobRole} from "@/core/interfaces/TeamPlanning";
import {JOB_ROLES, TimeRange, jobRoleForRank} from "@/team/planning";
import {MemberDay} from "@/planning/lib/weekPlanning";
import usePlanningRange, {WEEK_DAY_COUNT, mondayOf, weekDates} from "@/planning/composables/usePlanningRange";
import useHourCounter from "@/planning/composables/useHourCounter";
import {StaffingTargetInput} from "@/core/http/resources/TeamPlanningResource";
import PlanningTeamGrid from "@/planning/components/PlanningTeamGrid.vue";
import PlanningDayView from "@/planning/components/PlanningDayView.vue";
import PlanningMonthView from "@/planning/components/PlanningMonthView.vue";
import VigilancePanel from "@/planning/components/VigilancePanel.vue";
import AbsencesPanel, {AbsenceTab} from "@/planning/components/AbsencesPanel.vue";
import ShiftEditDialog from "@/planning/components/ShiftEditDialog.vue";
import StaffingTargetsDialog from "@/planning/components/StaffingTargetsDialog.vue";
import HourCounterDialog from "@/planning/components/HourCounterDialog.vue";
import HealthcenterOpeningHoursDialog from "@/healthcenter/components/HealthcenterOpeningHoursDialog.vue";
import useHealthcenterOpening from "@/healthcenter/composables/useHealthcenterOpening";
import {OpeningState, isOpeningFilled} from "@/agenda/lib/openingHours";
import AssistantLauncher from "@/ai-assistant/components/AssistantLauncher.vue";
import useAssistantScreen from "@/ai-assistant/composables/useAssistantScreen";
import useExperimentalFeatures from "@/core/composables/experimental-features/useExperimentalFeatures";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import {Popover, PopoverContent, PopoverTrigger} from "@/core/components/ui/popover";
import {
    CalendarDays,
    CalendarOff,
    CalendarRange,
    ChevronLeft,
    ChevronRight,
    Clock,
    CircleCheck,
    Filter,
    Info,
    ListTodo,
    TriangleAlert,
    UsersRound
} from "@lucide/vue";

const router = useRouter();
const {showSuccess, showError} = useToastStore();

const view = ref<'team' | 'day' | 'month' | 'absences'>('team');

/**
 * The absences panel is mounted on demand, so it reads this on creation:
 * the toolbar opens its list, the month view its timeline.
 */
const absencesTab = ref<AbsenceTab>('list');

const openAbsences = (tab: AbsenceTab) => {
    absencesTab.value = tab;
    view.value = 'absences';
};

/**
 * Absences go through `openAbsences`: assigning `view` directly would leave
 * `absencesTab` on whatever a previous entry point set, so the segment would
 * open the timeline while the button beside it opens the list.
 */
const selectView = (next: typeof view.value) => {
    if (next === 'absences') {
        openAbsences('list');

        return;
    }

    view.value = next;
};
const weekStart = ref<Dayjs>(mondayOf(dayjs().locale('fr')));
const selectedDate = ref<Dayjs>(dayjs().locale('fr').startOf('day'));
const dayFocus = ref<Dayjs | null>(null);
const hiddenRoles = ref<Set<JobRole>>(new Set());

const days = computed(() => weekDates(weekStart.value));

/** Fetched range: the shown week or month, always covering the 3 vigilance days. */
const rangeStart = computed(() => {
    const base = view.value === 'month' ? weekStart.value.startOf('month') : weekStart.value;

    return base.isBefore(dayjs().startOf('day')) ? base : dayjs().startOf('day');
});

const rangeEnd = computed(() => {
    const base = view.value === 'month' ? weekStart.value.endOf('month') : weekStart.value.add(WEEK_DAY_COUNT - 1, 'day');
    const vigilance = dayjs().startOf('day').add(2, 'day');

    return base.isAfter(vigilance) ? base : vigilance;
});

const {
    organizationId,
    members,
    schedulesByAccessId,
    targets,
    setTargets,
    resolveDay,
    refreshRange,
    loading,
    failed: rangeFailed,
    loaded: rangeLoaded,
    teamLoaded,
} = usePlanningRange(rangeStart, rangeEnd);

const {balanceByMember, balancesFailed, refreshBalances} = useHourCounter();

const {
    healthcenterId,
    opening,
    isLoading: openingLoading,
    error: openingError,
    reload: reloadOpening
} = useHealthcenterOpening();

/**
 * A failed load is only reported when nothing was ever read: a sheet kept
 * from an earlier load is still the pharmacy's hours.
 */
const openingState = computed<OpeningState>(() => {
    if (opening.value === null && openingError.value) {
        return 'failed';
    }

    if (openingLoading.value) {
        return 'loading';
    }

    return isOpeningFilled(opening.value) ? 'filled' : 'missing';
});

const openingDialogOpen = ref<boolean>(false);
const hourCounterOpen = ref<boolean>(false);
const hourCounterMemberId = ref<number | null>(null);

const openHourCounter = (memberId: number) => {
    hourCounterMemberId.value = memberId;
    hourCounterOpen.value = true;
};

/**
 * Both loads fail silently by design — the range keeps the previous period
 * and the balances fall back to zero — so the screen has to say so itself.
 * The hour counter especially: its own error banner lives inside a dialog
 * whose only door is a badge that a failed load makes disappear.
 */
const loadFailure = computed<string | null>(() => {
    // What the grid actually shows differs: a first load that failed has no
    // absence and no dated change at all, so a member on approved leave reads
    // as being at work — the opposite of trusting a previous period.
    const rangeMessage = rangeLoaded.value
        ? 'Les absences et modifications de cette période n\'ont pas pu être chargées : '
            + 'les horaires affichés sont ceux de la période précédente.'
        : 'Les absences et modifications n\'ont pas pu être chargées : la grille montre '
            + 'les horaires récurrents seuls, sans absence ni modification datée.';

    const messages: string[] = [];

    if (rangeFailed.value && balancesFailed.value) {
        messages.push(`${rangeMessage} Les soldes d'heures non plus.`);
    } else if (rangeFailed.value) {
        messages.push(rangeMessage);
    } else if (balancesFailed.value) {
        messages.push('Les soldes d\'heures n\'ont pas pu être chargés : les badges sont masqués.');
    }

    if (openingState.value === 'failed') {
        messages.push('Les horaires de la pharmacie n\'ont pas pu être chargés.');
    }

    return messages.length ? messages.join(' ') : null;
});

const retrying = ref<boolean>(false);

const retryFailedLoads = async () => {
    if (retrying.value) {
        return;
    }

    retrying.value = true;

    try {
        // No call rejects, and each keeps what it had on failure: the
        // banner staying put is the only signal, so the button must at least
        // show that something happened.
        await Promise.all([
            rangeFailed.value ? refreshRange() : Promise.resolve(),
            balancesFailed.value ? refreshBalances() : Promise.resolve(),
            openingState.value === 'failed' ? reloadOpening() : Promise.resolve(),
        ]);
    } finally {
        retrying.value = false;
    }
};

/** Staff shown in the grids, the role filter applied. */
const visibleMembers = computed(() => members.value.filter(member =>
    !hiddenRoles.value.has(jobRoleForRank(member.rankId) as JobRole)
));

const roleCounts = computed(() => JOB_ROLES.map(role => ({
    ...role,
    count: members.value.filter(member => jobRoleForRank(member.rankId) === role.value).length,
})));

const toggleRole = (role: JobRole) => {
    const next = new Set(hiddenRoles.value);

    if (next.has(role)) {
        next.delete(role);
    } else {
        next.add(role);
    }

    hiddenRoles.value = next;
};

const weekLabel = computed(() =>
    `${weekStart.value.format('DD MMM')} – ${weekStart.value.add(WEEK_DAY_COUNT - 1, 'day').format('DD MMM YYYY')}`
);

/**
 * What the assistant is told about this screen: the window on display, and the
 * day the pharmacist has narrowed it to when they have.
 *
 * Worded exactly as the toolbar words it. The pharmacist asks about what they
 * see, and an answer that opened on « semaine du 14 » while the screen says
 * « 14 sept – 19 sept » would read as an answer about something else.
 *
 * The absences panel comes first because it answers for the whole screen: it
 * hides the toolbar's date range and its day banner, so naming a week there
 * would describe a window nobody has in front of them.
 */
const assistantWindow = computed<string>(() => {
    if (view.value === 'absences') {
        return 'Absences & demandes';
    }

    if (dayFocus.value) {
        return dayFocus.value.format('dddd DD MMMM');
    }

    return view.value === 'month' ? weekStart.value.format('MMMM YYYY') : weekLabel.value;
});

useAssistantScreen(() => ({
    screen: 'Planning',
    window: assistantWindow.value
}));

/** The assistant is offered here only in experimental mode (Ctrl+X) for now. */
const {enabled: assistantOffered} = useExperimentalFeatures();

const moveWeek = (direction: 1 | -1) => {
    // Re-anchor on Monday: adding a month rarely lands on one.
    weekStart.value = mondayOf(weekStart.value.add(direction, view.value === 'month' ? 'month' : 'week'));
    dayFocus.value = null;
};

const goToday = () => {
    weekStart.value = mondayOf(dayjs().locale('fr'));
    selectedDate.value = dayjs().locale('fr').startOf('day');
    dayFocus.value = null;
};

const onDayClick = (date: Dayjs) => {
    dayFocus.value = dayFocus.value?.isSame(date, 'day') ? null : date;
};

/** From the vigilance panel: jump to the day view on the flagged date. */
const onShiftSlot = (date: Dayjs) => {
    view.value = 'day';
    selectedDate.value = date;
    dayFocus.value = null;

    if (!mondayOf(date).isSame(weekStart.value, 'day')) {
        weekStart.value = mondayOf(date);
    }
};

// ----- Shift exception editing -----

const editOpen = ref<boolean>(false);
const editContext = ref<{ day: MemberDay; rangeIndex: number; proposed: TimeRange } | null>(null);

const onEditRange = (payload: { day: MemberDay; rangeIndex: number; proposed: TimeRange }) => {
    editContext.value = payload;
    editOpen.value = true;
};

const saveException = async (payload: { starts_at: string; ends_at: string; reason: string; note: string }) => {
    const context = editContext.value;

    if (!context || !organizationId.value) {
        return;
    }

    const slots = context.day.ranges.map((range, index) =>
        index === context.rangeIndex
            ? {starts_at: payload.starts_at, ends_at: payload.ends_at}
            : {starts_at: range.starts_at, ends_at: range.ends_at}
    );

    try {
        if (context.day.exception) {
            await teamPlanningApi().updateShiftException(context.day.exception.id, {
                reason: payload.reason,
                note: payload.note || null,
                slots,
            });
        } else {
            await teamPlanningApi().createShiftException(organizationId.value, {
                user_access_point_id: context.day.member.id,
                date: context.day.date.format('YYYY-MM-DD'),
                reason: payload.reason,
                note: payload.note || null,
                slots,
            });
        }
    } catch {
        showError({content: 'La plage horaire n\'a pas pu être modifiée'});
        editOpen.value = false;

        return;
    }

    // The save is done regardless of what the refreshes below do: report it.
    // The balances move too — the server mirrors the exception in the counter.
    showSuccess({content: 'La plage horaire a été modifiée'});
    editOpen.value = false;
    await Promise.all([refreshRange(), refreshBalances()]);
};

// ----- Staffing targets -----

const targetsOpen = ref<boolean>(false);
const targetsSaving = ref<boolean>(false);

const saveTargets = async (draft: StaffingTargetInput[]) => {
    if (!organizationId.value) {
        return;
    }

    targetsSaving.value = true;

    try {
        setTargets(await teamPlanningApi().syncStaffingTargets(organizationId.value, draft));
        showSuccess({content: 'L\'effectif cible a été enregistré'});
        targetsOpen.value = false;
    } catch {
        showError({content: 'L\'effectif cible n\'a pas pu être enregistré'});
    } finally {
        targetsSaving.value = false;
    }
};
</script>

<template>
  <div class="p-4">
    <!-- Stands under the page title, which the top bar draws. -->
    <div
      v-if="openingState === 'filled'"
      class="mb-2 flex items-center gap-1.5 text-[11px] font-medium text-green-700"
    >
      <CircleCheck class="size-3.5" />
      Horaires de la pharmacie renseignés
    </div>

    <!-- Toolbar -->
    <div class="mb-3.5 flex flex-wrap items-center gap-2.5">
      <!-- The absences view spans no week: its calendar tab navigates months
           on its own, and the week arrows would move nothing. -->
      <template v-if="view !== 'absences'">
        <div class="flex items-center overflow-hidden rounded-lg border border-gray-200 bg-white">
          <button class="flex size-8 items-center justify-center border-r border-gray-200 text-gray-600 hover:bg-gray-50" @click="moveWeek(-1)">
            <ChevronLeft class="size-4" />
          </button>
          <div class="whitespace-nowrap px-3 text-xs font-semibold capitalize">
            {{ view === 'month' ? weekStart.format('MMMM YYYY') : weekLabel }}
          </div>
          <button class="flex size-8 items-center justify-center border-l border-gray-200 text-gray-600 hover:bg-gray-50" @click="moveWeek(1)">
            <ChevronRight class="size-4" />
          </button>
        </div>
        <button class="h-8 rounded-lg border border-gray-200 bg-white px-3 text-xs font-medium text-gray-700 hover:border-gray-300" @click="goToday">
          Aujourd'hui
        </button>
      </template>
      <button
        class="flex h-8 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-xs font-semibold text-primary hover:border-primary/40"
        @click="router.push('/team')"
      >
        <Clock class="size-3.5" />
        Horaires de l'équipe
      </button>
      <AssistantLauncher v-if="assistantOffered" compact />
      <button
        class="flex h-8 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-xs font-semibold text-primary hover:border-primary/40"
        @click="targetsOpen = true"
      >
        <UsersRound class="size-3.5" />
        Effectif cible
      </button>
      <button
        class="flex h-8 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-xs font-semibold text-primary hover:border-primary/40"
        @click="router.push('/team-tasks')"
      >
        <ListTodo class="size-3.5" />
        Tâches
      </button>
      <button
        v-if="view !== 'absences'"
        class="flex h-8 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-xs font-semibold text-primary hover:border-primary/40"
        @click="openAbsences('list')"
      >
        <CalendarOff class="size-3.5" />
        Gérer les absences
      </button>

      <div class="flex rounded-lg bg-gray-100 p-0.5">
        <button
          v-for="tab in ([
            {key: 'team', label: 'Par collaborateur', icon: UsersRound},
            {key: 'day', label: 'Par journée', icon: CalendarDays},
            {key: 'month', label: 'Mois', icon: CalendarRange},
            {key: 'absences', label: 'Absences', icon: CalendarOff}
          ] as const)"
          :key="tab.key"
          class="flex h-7 items-center gap-1.5 rounded-md px-2.5 text-xs font-semibold transition-colors"
          :class="view === tab.key ? 'bg-white text-primary shadow-sm' : 'text-gray-500 hover:text-gray-700'"
          @click="selectView(tab.key)"
        >
          <component :is="tab.icon" class="size-3.5" />
          {{ tab.label }}
        </button>
      </div>

      <div class="flex-1" />

      <Popover v-if="view !== 'absences'">
        <PopoverTrigger as-child>
          <button class="flex h-8 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-700 hover:border-gray-300">
            <Filter class="size-3.5" />
            Filtres
            <span
              v-if="hiddenRoles.size"
              class="rounded-full bg-primary px-1.5 text-[9px] font-bold text-white"
            >
              {{ hiddenRoles.size }}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" class="w-56 p-3">
          <div class="mb-2 text-[10.5px] font-semibold text-gray-400">
            Rôles affichés dans le planning
          </div>
          <button
            v-for="role in roleCounts"
            :key="role.value"
            class="flex w-full items-center gap-2 rounded-md px-1.5 py-1.5 text-left hover:bg-gray-50"
            :class="hiddenRoles.has(role.value) ? 'opacity-40' : ''"
            @click="toggleRole(role.value)"
          >
            <span class="size-3 rounded" :style="{backgroundColor: role.color}" />
            <span class="flex-1 text-[11.5px] font-medium">{{ role.label }}</span>
            <span class="text-[9.5px] text-gray-400">{{ role.count }}</span>
          </button>
          <button
            class="mt-2 h-7 w-full rounded-lg border border-gray-200 text-[10.5px] font-semibold text-primary hover:border-primary/40"
            @click="hiddenRoles = new Set()"
          >
            Tout afficher
          </button>
        </PopoverContent>
      </Popover>
    </div>

    <AbsencesPanel
      v-if="view === 'absences'"
      :members="members"
      :initial-tab="absencesTab"
      :refresh-planning="refreshRange"
    />

    <template v-else>
      <VigilancePanel
        :members="members"
        :resolve-day="resolveDay"
        :targets="targets"
        @shift-slot="onShiftSlot"
      />

      <!-- The "Prochaine garde" card and the traffic row live here once their chantiers open. -->

      <div
        v-if="loadFailure"
        class="mb-2.5 flex flex-wrap items-center gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2"
      >
        <TriangleAlert class="size-4 shrink-0 text-amber-700" />
        <span class="flex-1 text-[11.5px] font-medium text-amber-900">
          {{ loadFailure }}
        </span>
        <button
          class="h-7 rounded-lg border border-amber-300 bg-white px-2.5 text-[10.5px] font-semibold text-amber-900 hover:border-amber-500 disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="retrying"
          @click="retryFailedLoads"
        >
          {{ retrying ? 'Nouvelle tentative…' : 'Réessayer' }}
        </button>
      </div>

      <div
        v-if="openingState === 'missing'"
        class="mb-2.5 flex flex-wrap items-center gap-2.5 rounded-lg border border-primary/20 bg-primary/5 px-3.5 py-2"
      >
        <Info class="size-4 shrink-0 text-primary" />
        <div class="flex-1">
          <div class="text-[11.5px] font-semibold text-primary">
            Les horaires de la pharmacie ne sont pas renseignés
          </div>
          <div class="text-[11px] text-gray-600">
            Renseignez-les pour que le calendrier et la réservation en ligne ne proposent que des créneaux où la pharmacie est ouverte.
          </div>
        </div>
        <button
          class="h-7 rounded-lg bg-primary px-2.5 text-[10.5px] font-semibold text-white hover:bg-primary/90"
          @click="openingDialogOpen = true"
        >
          Renseigner les horaires
        </button>
      </div>

      <div v-if="dayFocus" class="mb-2.5 flex items-center gap-2.5 rounded-lg border border-primary/20 bg-primary/5 px-3.5 py-2">
        <Filter class="size-4 text-primary" />
        <span class="flex-1 text-[11.5px] font-semibold capitalize text-primary">
          Membres présents le {{ dayFocus.format('dddd DD MMMM') }} uniquement
        </span>
        <button
          class="h-7 rounded-lg border border-primary bg-white px-2.5 text-[10.5px] font-semibold text-primary"
          @click="dayFocus = null"
        >
          Toute l'équipe
        </button>
      </div>

      <!-- Only the first load blanks the screen: a refresh keeps the grid it
           already has, so the banner above never claims hours are shown while
           the spinner has replaced them. -->
      <BaseFlexSpinner v-if="loading && !rangeLoaded" />
      <template v-else>
        <div v-if="view === 'team'" class="overflow-x-auto pb-1">
          <PlanningTeamGrid
            :days="days"
            :members="visibleMembers"
            :coverage-members="members"
            :resolve-day="resolveDay"
            :targets="targets"
            :schedules="schedulesByAccessId"
            :day-focus="dayFocus"
            :hour-balances="balanceByMember"
            :opening="opening"
            :opening-state="openingState"
            :team-loaded="teamLoaded"
            @edit-range="onEditRange"
            @day-click="onDayClick"
            @add-member="router.push('/team')"
            @open-hour-counter="openHourCounter"
            @edit-opening="openingDialogOpen = true"
          />
        </div>
        <PlanningDayView
          v-else-if="view === 'day'"
          :days="days"
          :selected-date="selectedDate"
          :members="visibleMembers"
          :coverage-members="members"
          :resolve-day="resolveDay"
          @select-date="date => selectedDate = date"
          @edit-range="onEditRange"
        />
        <PlanningMonthView
          v-else-if="view === 'month'"
          :anchor="weekStart"
          :members="members"
          :resolve-day="resolveDay"
          @show-absences="openAbsences('calendar')"
        />
      </template>
    </template>

    <ShiftEditDialog
      v-model:open="editOpen"
      :member-name="editContext?.day.member.user?.name ?? ''"
      :day-label="editContext?.day.date.format('dddd DD MMMM') ?? ''"
      :range="editContext?.proposed ?? null"
      :original="editContext ? editContext.day.ranges[editContext.rangeIndex] : null"
      @save="saveException"
    />

    <StaffingTargetsDialog
      v-model:open="targetsOpen"
      :targets="targets"
      :saving="targetsSaving"
      @save="saveTargets"
    />

    <HourCounterDialog
      v-model:open="hourCounterOpen"
      :members="members"
      :member-id="hourCounterMemberId"
    />

    <HealthcenterOpeningHoursDialog
      v-if="healthcenterId"
      v-model:open="openingDialogOpen"
      :healthcenter-id="healthcenterId"
      :opening="opening"
    />
  </div>
</template>
